<script setup lang="ts">
    import { ref, computed, onMounted, nextTick } from 'vue'
    import { useRoute } from 'vue-router'
    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"

    import type { ProjectPhaseDefinitionsPageReadModel, ProjectPhaseDefinitionReadModel } from '@Quest/Strategies/readmodels'
    import { useProjectPhaseDefinitionsPageQueryV1 } from '@Quest/Strategies/queries-vue-composables'
    import { useCreateProjectPhaseDefinitionCommandV1, useDeleteProjectPhaseDefinitionCommandV1, useRenameProjectPhaseDefinitionCommandV1 } from '@Quest/Strategies/commands-vue-composables'
    import { guid } from '@common/Guid'
    import type { ValidationError } from '@common/types'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    const newPhaseName = ref("")
    const newPhaseId = ref(guid())
    const insertAfterId = ref<string | null>(null)

    const editingId = ref<string | null>(null)
    const editName = ref("")
    const renameInputs = ref<Record<string, HTMLInputElement | null>>({})


    const {
        runProjectPhaseDefinitionsPageQueryV1,
        modelProjectPhaseDefinitionsPageQueryV1
    } = useProjectPhaseDefinitionsPageQueryV1(workspaceId, strategyId)

    const rm = computed(() => modelProjectPhaseDefinitionsPageQueryV1.value as ProjectPhaseDefinitionsPageReadModel)

    const {
        invokeCreateProjectPhaseDefinitionCommandV1,
        validationErrorsCreateProjectPhaseDefinitionCommandV1: createErrors,
        onSucceedCreateProjectPhaseDefinitionCommandV1
    } = useCreateProjectPhaseDefinitionCommandV1(workspaceId, strategyId, newPhaseId, newPhaseName, insertAfterId)

    const {
        invokeDeleteProjectPhaseDefinitionCommandV1,
        onSucceedDeleteProjectPhaseDefinitionCommandV1
    } = useDeleteProjectPhaseDefinitionCommandV1(workspaceId, strategyId)

    const {
        invokeRenameProjectPhaseDefinitionCommandV1,
        validationErrorsRenameProjectPhaseDefinitionCommandV1: renameErrors,
        onSucceedRenameProjectPhaseDefinitionCommandV1
    } = useRenameProjectPhaseDefinitionCommandV1(workspaceId, strategyId, editingId, editName)

    function getError(errors: ValidationError[] | undefined, context: string)
    {
        return (errors ?? []).find(e => e.Context === context)?.Error
    }

    const createNameError = computed(() => getError(createErrors.value, "Name"))
    const renameNameError = computed(() =>
    {
        if (!editingId.value) return null
        return getError(renameErrors.value, "Name")
    })


    function handleDeleteProjectPlanTemplateCommand(t: ProjectPhaseDefinitionReadModel)
    {
        if (confirm("Are you sure?"))
        {
            invokeDeleteProjectPhaseDefinitionCommandV1({ ProjectPhaseDefinitionId: t.Id })
        }
    }

    function createPhase()
    {
        invokeCreateProjectPhaseDefinitionCommandV1()
    }

    function startRename(p: ProjectPhaseDefinitionReadModel)
    {
        editingId.value = p.Id
        editName.value = p.Name
        renameErrors.value = []

        nextTick(() =>
        {
            const input = renameInputs.value[p.Id]
            input?.focus()
            input?.select()
        })
    }

    function setRenameInputRef(id: string)
    {
        return (el: HTMLInputElement | null) =>
        {
            renameInputs.value[id] = el
        }
    }

    function cancelRename()
    {
        editingId.value = null
        renameErrors.value = []
    }

    function confirmRename()
    {
        invokeRenameProjectPhaseDefinitionCommandV1()        
    }

    onMounted(() =>
    {
        document.title = "Project phase definitions"
        runProjectPhaseDefinitionsPageQueryV1()
    })

    onSucceedCreateProjectPhaseDefinitionCommandV1(() =>
    {
        newPhaseId.value = guid()
        newPhaseName.value = ""

        runProjectPhaseDefinitionsPageQueryV1()
    })

    onSucceedDeleteProjectPhaseDefinitionCommandV1(() =>
    {
        runProjectPhaseDefinitionsPageQueryV1()
    })

    onSucceedRenameProjectPhaseDefinitionCommandV1(() =>
    {
        editingId.value = null

        runProjectPhaseDefinitionsPageQueryV1()
    })

</script>

<template>
    <div>
        <CorporateNavigation ActivePage="strategy" />

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Project phase definitions</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <!--<button type="button" class="btn btn-outline-success" style="padding: 0.7em 2em;" @click="showProjectPlanTemplateCreator">Create a template</button>-->
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="mt-3">

                <!-- Add new phase -->
                <div class="card mb-4">
                    <div class="card-body">

                        <h5 class="mb-3">Add new phase</h5>

                        <form @submit.prevent="createPhase">
                            <div class="row">
                                <div class="col-md-4">
                                    <input v-model.trim="newPhaseName"
                                           class="form-control"
                                           :class="{ 'is-invalid': createNameError }"
                                           placeholder="Phase name"
                                           required
                                           minlength="1"
                                           data-testid="project-phase-definition-name" />

                                    <div v-if="createNameError" class="invalid-feedback">
                                        {{ createNameError  }}
                                    </div>
                                </div>

                                <div class="col-md-4">
                                    <select v-model="insertAfterId" class="form-control" data-testid="project-phase-definition-insert-after">
                                        <option :value="null">Insert at beginning</option>
                                        <option v-for="p in rm.Definitions"
                                                :key="p.Id"
                                                :value="p.Id">
                                            After {{ p.Name }}
                                        </option>
                                    </select>
                                </div>

                                <div class="col-md-4">
                                    <button type="submit"
                                            class="btn btn-success">
                                        Add phase
                                    </button>
                                </div>
                            </div>
                        </form>

                    </div>
                </div>

                <!-- Existing phases -->
                <div class="list-group">

                    <div v-for="(p, index) in rm.Definitions"
                         :key="p.Id"
                         class="list-group-item d-flex justify-content-between align-items-center"
                         :data-testid="`project-phase-definition-row-${index}`">

                        <div class="d-flex align-items-center">

                            <strong class="mr-2">{{ index + 1 }}.</strong>

                            <span v-if="editingId !== p.Id">
                                {{ p.Name }}
                            </span>

                            <form v-else
                                  :id="'rename-form-' + p.Id"
                                  class="d-flex flex-column"
                                  @submit.prevent="confirmRename">

                                <input v-model.trim="editName"
                                       class="form-control rename-input"
                                       style="width:250px"
                                       :class="{ 'is-invalid': renameNameError }"
                                       required
                                       minlength="1"
                                       @keydown.esc="cancelRename"
                                       :ref="setRenameInputRef(p.Id)"
                                       :data-testid="`project-phase-definition-rename-input-${index}`"/>

                                <div v-if="renameNameError" class="invalid-feedback">
                                    {{ renameNameError }}
                                </div>

                            </form>

                        </div>

                        <div class="d-flex gap-2">

                            <!-- Normal mode -->
                            <template v-if="editingId !== p.Id">

                                <button class="btn btn-sm btn-outline-primary"
                                        :data-testid="`project-phase-definition-rename-${index}`"
                                        @click="startRename(p)">
                                    Rename
                                </button>

                                <button class="btn btn-sm btn-outline-danger"
                                        :data-testid="`project-phase-definition-delete-${index}`"
                                        @click="handleDeleteProjectPlanTemplateCommand(p)">
                                    Delete
                                </button>

                            </template>

                            <!-- Edit mode -->
                            <template v-else>

                                <button class="btn btn-sm btn-success"
                                         :data-testid="`project-phase-definition-save-${index}`"
                                         :form="'rename-form-' + p.Id"
                                        type="submit">
                                    Save
                                </button>

                                <button class="btn btn-sm btn-secondary"
                                        :data-testid="`project-phase-definition-cancel-${index}`"
                                        type="button"
                                        @click="cancelRename">
                                    Cancel
                                </button>

                            </template>

                        </div>

                    </div>

                </div>

            </div>
        </div>
    </div>
</template>